/**
 * The lead form. Backs both the short hero form and the full footer form.
 *
 * Contract with the backend is unchanged:
 *   PUT https://www.applates.click/api/business/contactUs
 *   { phonenumber, name, businessname, date }
 */
import { ref, reactive, computed } from "vue";
import { track } from "./useAnalytics";

const ENDPOINT = "https://www.applates.click/api/business/contactUs";
const MAX_ATTEMPTS_PER_DAY = 4;
const STORAGE_PREFIX = "applates:contact:";

export const FormState = {
  Idle: "idle",
  Loading: "loading",
  Success: "success",
  Blocked: "blocked",
  Error: "error",
};

/** dd/mm/yyyy - hh:mm, the shape the API already receives. */
function formatTimestamp(d = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}` +
    ` - ${d.getHours()}:${pad(d.getMinutes())}`
  );
}

function dayKey(d = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Israeli mobile numbers, typed however people actually type them:
 * 0541234567, 054-123-4567, 054 123 4567, +972541234567, 972541234567.
 * Returns the canonical 10-digit local form, or null.
 */
export function normalisePhone(raw) {
  let digits = String(raw || "").replace(/\D/g, "");

  if (digits.startsWith("972")) digits = `0${digits.slice(3)}`;

  // Landlines are 9 digits (e.g. 03-1234567), mobiles 10.
  if (/^0[23489]\d{7}$/.test(digits)) return digits;
  if (/^05\d{8}$/.test(digits)) return digits;

  return null;
}

/**
 * localStorage is trivially cleared, so this only stops accidental
 * double-submits. Real rate limiting belongs on the server.
 *
 * The old version stored `attempts + 1` where attempts was a string, so the
 * second submit wrote "11" and the visitor was blocked on their third try.
 */
function readAttempts(key) {
  try {
    const parsed = parseInt(localStorage.getItem(key), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  } catch {
    return 0;
  }
}

function writeAttempts(key, value) {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // Private browsing. Not worth failing a submit over.
  }
}

/**
 * @param {object} [options]
 * @param {string} [options.formId] identifies the form in analytics
 * @param {boolean} [options.requireBusiness] the short hero form drops it
 */
export function useLeadForm(options = {}) {
  const { formId = "lead", requireBusiness = true } = options;

  const fields = reactive({ name: "", businessName: "", phone: "" });
  const errors = reactive({ name: "", businessName: "", phone: "" });
  const state = ref(FormState.Idle);
  const started = ref(false);

  const isLoading = computed(() => state.value === FormState.Loading);
  const isDone = computed(() => state.value === FormState.Success);

  /** First interaction with the form — the top of the funnel. */
  function onFirstInput() {
    if (started.value) return;
    started.value = true;
    track("lead_form_start", { form_id: formId });
  }

  function validateField(field) {
    switch (field) {
      case "name":
        errors.name =
          fields.name.trim().length < 2 ? "נא להזין שם מלא" : "";
        break;
      case "businessName":
        errors.businessName =
          requireBusiness && fields.businessName.trim().length < 2
            ? "נא להזין את שם העסק"
            : "";
        break;
      case "phone":
        errors.phone = normalisePhone(fields.phone)
          ? ""
          : "נא להזין מספר טלפון תקין";
        break;
    }
    return !errors[field];
  }

  function validate() {
    const checks = ["name", "phone"];
    if (requireBusiness) checks.push("businessName");
    // Run every check so all errors surface at once.
    return checks.map(validateField).every(Boolean);
  }

  function firstInvalidField() {
    return ["name", "businessName", "phone"].find((f) => errors[f]) || null;
  }

  async function submit() {
    if (isLoading.value) return { ok: false };

    track("lead_form_submit", { form_id: formId });

    if (!validate()) {
      state.value = FormState.Idle;
      track("lead_form_error", { form_id: formId, reason: "validation" });
      return { ok: false, focus: firstInvalidField() };
    }

    const key = `${STORAGE_PREFIX}${dayKey()}`;
    const attempts = readAttempts(key);
    if (attempts >= MAX_ATTEMPTS_PER_DAY) {
      state.value = FormState.Blocked;
      track("lead_form_error", { form_id: formId, reason: "rate_limit" });
      return { ok: false };
    }

    state.value = FormState.Loading;

    try {
      const response = await fetch(ENDPOINT, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phonenumber: normalisePhone(fields.phone),
          name: fields.name.trim(),
          businessname: fields.businessName.trim(),
          date: formatTimestamp(),
        }),
      });

      const data = await response.json();

      if (data?.result === "successfully") {
        writeAttempts(key, attempts + 1);
        state.value = FormState.Success;
        track("lead_form_success", { form_id: formId });
        return { ok: true };
      }

      state.value = FormState.Error;
      track("lead_form_error", { form_id: formId, reason: "server" });
      return { ok: false };
    } catch {
      state.value = FormState.Error;
      track("lead_form_error", { form_id: formId, reason: "network" });
      return { ok: false };
    }
  }

  function reset() {
    state.value = FormState.Idle;
  }

  return {
    fields,
    errors,
    state,
    isLoading,
    isDone,
    FormState,
    onFirstInput,
    validateField,
    submit,
    reset,
  };
}
